import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { formValueSelector, getFormValues } from 'redux-form';
import { push } from 'redux-first-history';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import api from '../../../core/api';
import {
  BaseRationData,
  CreateRationPayload,
  EntryMethodsKeys,
  IngredientInRecipeCreation,
  TargetWeights,
} from '../../application/recipes/appRecipes.types';
import { LoadersNames } from '../../ui/ui.constants';
import { getFarmsListRequest } from '../farms/farms.actions';
import {
  createRecipe,
  deleteRecipe,
  duplicateRecipe,
  editRecipe,
  getRecipe,
  getRecipeList,
  renameRecipe,
} from './recipes.actions';
import { Forms } from '../../form/form.selector';
import {
  appRecipeSelector,
  ingredientsSelector,
  recipesSelector,
  selectedSelector,
  settingsSelector,
} from '../../selectors';
import { editIngredientRequest } from '../ingredients/ingredients.actions';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { productRoutesPaths } from '../../../routes/ManageProducts/productRoutesPaths';
import { loadMixersActions } from '../../actions';
import { refetchFeedingsSaga } from '../feeding/feeding.saga';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { setSelectedRecipe } from '../../selected/selected.actions';
import { Recipe } from './recipes.types';
import { compose403ErrorMessage } from '../../../core/helpers/utils/sagaUtils';
import { formatDate } from '../../../core/helpers/utils/dateUtils';
import { RecipeTransitions } from '../../../routes/ManageProducts/Recipes/shared/RecipeLeftSidebar/RecipeTransitionField/RecipeTransitionField';

const composeCreationPayload = (
  formValues: any,
  ingredientsInRecipe: IngredientInRecipeCreation[],
  baseRation: BaseRationData,
  method: EntryMethodsKeys,
  targetWeightType: TargetWeights,
  waterId: string,
  isRefusal: boolean,
  transitionDays: number[],
): CreateRationPayload => {
  const ingredients = ingredientsInRecipe.map((ing) => {
    const res = {
      ingredientId: ing.id,
      amounts:
        method === EntryMethodsKeys.WEIGHT && (targetWeightType === TargetWeights.ACTUAL || ing.id === waterId)
          ? ing.actualLoad
          : undefined,
      dryAmounts:
        method === EntryMethodsKeys.WEIGHT && targetWeightType === TargetWeights.DRY && ing.id !== waterId
          ? ing.dryMatter
          : undefined,
      loadPercs:
        method === EntryMethodsKeys.PERCENTAGE && (targetWeightType === TargetWeights.ACTUAL || ing.id === waterId)
          ? ing.actualLoadTotalPercentage
          : undefined,
      dryLoadPercs:
        method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY && ing.id !== waterId
          ? ing.dryMatterTotalPercentage
          : undefined,
      mixingValue: ing.mixerData.mixingValue,
      mixingTypeCode: ing.mixerData.mixingTypeCode,
      mixingStartTypeCode: ing.mixerData.mixingStartTypeCode,
      mixingRPM: ing.mixerData.mixingRPM,
      mixingStartValue: ing.mixerData.mixingStartValue,
    } as any;
    if (ing.ownId) {
      res.id = ing.ownId;
    }
    if (isRefusal) {
      res.refusalSubstitute = ing.refusalSubstitute;
    }
    return res;
  });
  return {
    displayName: formValues.displayName,
    description: formValues.description,
    adjOnDM: formValues.adjOnDM,
    notInUse: formValues.notInUse,
    density: formValues.density,
    autoAdjWater: formValues.autoAdjWater,
    percEntryMethod: targetWeightType === TargetWeights.DRY ? 1 : 2,
    amountDays: transitionDays,
    ingredients,
    targetDMPerc: formValues.autoAdjWater ? Number(formValues.targetDMPerc) : 0,
    baseRationId: baseRation.ration ? baseRation.ration.id : undefined,
    baseRationPerc: baseRation.ration ? baseRation.percentage : undefined,
    baseRationAmounts:
      baseRation.ration && method === EntryMethodsKeys.WEIGHT && targetWeightType === TargetWeights.ACTUAL
        ? [baseRation?.actualLoad]
        : undefined,
    baseRationDryAmounts:
      baseRation.ration && method === EntryMethodsKeys.WEIGHT && targetWeightType === TargetWeights.DRY
        ? [baseRation?.dryMatter]
        : undefined,
    baseRationLoadPercs:
      baseRation.ration && method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL
        ? [baseRation?.actualLoadTotalPercentage]
        : undefined,
    baseRationDryLoadPercs:
      baseRation.ration && method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY
        ? [baseRation?.dryMatterTotalPercentage]
        : undefined,
    rationTypeCode: formValues.recipeType,
    finalDateBeforeTransition:
      typeof formValues.finalDateBeforeTransition !== 'undefined' &&
      formValues.recipeTransition === RecipeTransitions.FIXED_DATES
        ? formatDate(formValues.finalDateBeforeTransition, 'yyyy-MM-dd')
        : undefined,
  };
};

export function* getRecipeListSaga() {
  yield put(showLoader(LoadersNames.fetchRecipes));
  try {
    const result = yield call(api.recipes.getRecipeList);
    yield put(
      getRecipeList.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchRecipes));
}

export function* getRecipeSaga({ payload }: ReturnType<typeof getRecipe.started>) {
  yield put(showLoader(LoadersNames.getRecipe));
  try {
    const premixId = yield select(selectedSelector.getSelectedRecipeId);
    const id = payload.id ? payload.id : premixId;
    if (id) {
      const result = yield call(api.recipes.getRecipe, payload.id ? payload.id : premixId);

      yield put(
        getRecipe.done({
          params: {},
          result,
        }),
      );
      if (payload.onSuccess) {
        payload.onSuccess();
      }
      if (result.baseRationId) {
        const baseRation = yield call(api.recipes.getRecipe, result.baseRationId);

        yield put(
          getRecipe.done({
            params: {},
            result: baseRation,
          }),
        );
      }
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getRecipe));
}

export function* createRecipeSaga(action: ReturnType<typeof createRecipe.started>) {
  yield put(showLoader(LoadersNames.createRecipe));
  try {
    const formName = action.payload;
    const recipeFormValues = yield select(getFormValues(Forms.recipeForm));
    const recipeIngredientsFormvValues = yield select(getFormValues(Forms.recipeIngredientsForm));
    const ingredientsInRecipe: IngredientInRecipeCreation[] = yield select(appRecipeSelector.getIngredientsInRecipe);
    const method = yield select(appRecipeSelector.getMethod);
    const targetActualLoadWeight = yield select(appRecipeSelector.getTargetActualLoadWeight);
    const targetDryMatterWeight = yield select(appRecipeSelector.getTargetDryMatterWeight);
    const waterId = yield select(ingredientsSelector.getWaterId);
    const baseRation = yield select(appRecipeSelector.getBaseRationData);
    const isRefusal = yield select(appRecipeSelector.isRefusalRecipe);
    const transitionDays = yield select(appRecipeSelector.getTransitionDays);
    const targetWeightType = recipeIngredientsFormvValues?.targetWeightType;
    const payload = composeCreationPayload(
      recipeFormValues,
      ingredientsInRecipe,
      baseRation,
      method,
      targetWeightType,
      waterId,
      isRefusal,
      transitionDays,
    );

    if (method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL) {
      payload.amounts = targetActualLoadWeight;
    }

    if (method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY) {
      payload.dryAmounts = targetDryMatterWeight;
    }

    const result = yield call(api.recipes.createRecipe, payload, formName);

    yield put(setSelectedRecipe(result.id));

    yield put(
      createRecipe.done({
        params: formName,
        result,
      }),
    );
    yield put(notificationSuccess('products:add_recipe_success'));
    yield put(push(productRoutesPaths.recipes));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createRecipe));
}

export function* duplicateRecipeSaga(action: ReturnType<typeof duplicateRecipe.started>) {
  yield put(showLoader(LoadersNames.createRecipe));
  try {
    const { recipeId, description, displayName, formName } = action.payload;
    const { meta } = action;
    let recipe: Recipe = (yield select(recipesSelector.getRecipeById))(recipeId);
    const waterId = yield select(ingredientsSelector.getWaterId);
    const method = yield select(settingsSelector.getRationEntryMethod);
    const { numberOfIngredients, ingredients } = recipe;

    if (numberOfIngredients !== 0 && !ingredients) {
      recipe = yield call(api.recipes.getRecipe, recipeId);
    }

    const {
      id,
      hasPremix,
      numberOfIngredients: ingNumber,
      numberOfDays,
      finalAmount,
      finalDryAmount,
      finalPricePerCow,
      finalDryMatterPerc,
      finalAnimalPrice,
      finalWeightPrice,
      finalPricePerWt,
      premixGroupId,
      premixIngredientId,
      roughageDMPercs,
      byproductDMPercs,
      concentrateDMPercs,
      weightPrices,
      dryMatterPercs,
      animalPrices,
      transitionAllowed,
      ...duplicatedRecipe
    } = recipe;

    const payload = {
      ...duplicatedRecipe,
      displayName,
      description,
      ingredients: recipe.ingredients.map((ing) => {
        const { premix, amounts, dryAmounts, loadPercs, dryLoadPercs, toleranceWeight, ...newIng } = {
          ...ing,
          id: undefined,
        };
        const isWater = waterId === ing.ingredientId;
        return {
          ...newIng,
          amounts: method === EntryMethodsKeys.WEIGHT ? amounts : undefined,
          loadPercs:
            method === EntryMethodsKeys.PERCENTAGE && (recipe.percEntryMethod === TargetWeights.ACTUAL || isWater)
              ? loadPercs
              : undefined,
          dryLoadPercs:
            method === EntryMethodsKeys.PERCENTAGE && recipe.percEntryMethod === TargetWeights.DRY && !isWater
              ? dryLoadPercs
              : undefined,
        };
      }),
    };
    const result = yield call(api.recipes.createRecipe, payload, formName);

    yield put(setSelectedRecipe(result.id));

    yield put(
      createRecipe.done({
        params: formName,
        result,
      }),
    );

    if (meta && meta.onSuccess) {
      meta.onSuccess();
    }

    yield put(notificationSuccess('products:add_recipe_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createRecipe));
}

export function* editRecipeSaga(action: ReturnType<typeof editRecipe.started>) {
  yield put(showLoader(LoadersNames.editRecipe));
  try {
    const formName = action.payload;
    const recipeFormValues = yield select(getFormValues(Forms.recipeForm));
    const recipeIngredientsFormvValues = yield select(getFormValues(Forms.recipeIngredientsForm));
    const ingredientsInRecipe: IngredientInRecipeCreation[] = yield select(appRecipeSelector.getIngredientsInRecipe);
    const baseRation = yield select(appRecipeSelector.getBaseRationData);
    const method = yield select(appRecipeSelector.getMethod);
    const targetActualLoadWeight = yield select(appRecipeSelector.getTargetActualLoadWeight);
    const targetDryMatterWeight = yield select(appRecipeSelector.getTargetDryMatterWeight);
    const waterId = yield select(ingredientsSelector.getWaterId);
    const isRefusal = yield select(appRecipeSelector.isRefusalRecipe);
    const transitionDays = yield select(appRecipeSelector.getTransitionDays);
    const targetWeightType = recipeIngredientsFormvValues?.targetWeightType;
    const payload = composeCreationPayload(
      recipeFormValues,
      ingredientsInRecipe,
      baseRation,
      method,
      targetWeightType,
      waterId,
      isRefusal,
      transitionDays,
    );

    if (method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.ACTUAL) {
      payload.amounts = targetActualLoadWeight;
    }

    if (method === EntryMethodsKeys.PERCENTAGE && targetWeightType === TargetWeights.DRY) {
      payload.dryAmounts = targetDryMatterWeight;
    }

    const recipeId = yield select(selectedSelector.getSelectedRecipeId);
    const result = yield call(api.recipes.editRecipe, recipeId, payload, formName);

    yield put(
      editRecipe.done({
        params: formName,
        result,
      }),
    );

    yield put(loadMixersActions.getLoadMixerPremixesList.started({}));

    yield put(notificationSuccess('products:edit_recipe_success'));
    yield put(push(productRoutesPaths.recipes));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editRecipe));
}

export function* renameRecipeSaga(action: ReturnType<typeof renameRecipe.started>) {
  yield put(showLoader(LoadersNames.renameRecipe));
  try {
    const formName = action.payload;
    const recipeRenameFormValue = yield select(formValueSelector(Forms.recipeRenameForm), 'description');

    const recipeId = yield select(selectedSelector.getSelectedRecipeId);
    const result = yield call(api.recipes.editRecipe, recipeId, { description: recipeRenameFormValue }, formName);

    yield put(
      editRecipe.done({
        params: formName,
        result,
      }),
    );
    yield put(notificationSuccess('products:rename_recipe_success'));
    yield put(push(productRoutesPaths.recipes));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.renameRecipe));
}

export function* deleteRecipeSaga(action: ReturnType<typeof renameRecipe.started>) {
  try {
    const formName = action.payload;
    const recipeId = yield select(selectedSelector.getSelectedRecipeId);
    yield call(api.recipes.deleteRecipe, recipeId, formName);
    yield put(
      deleteRecipe.done({
        params: formName,
        result: recipeId,
      }),
    );
    yield put(notificationSuccess('products:delete_recipe_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* watchRecipesSaga() {
  yield all([
    yield takeLatest(createRecipe.started, createRecipeSaga),
    yield takeLatest(duplicateRecipe.started, duplicateRecipeSaga),
    yield takeLatest(editRecipe.started, editRecipeSaga),
    yield takeLatest(editIngredientRequest.done, getRecipeListSaga),
    yield takeLatest(getRecipe.started, getRecipeSaga),
    yield takeLatest(deleteRecipe.started, deleteRecipeSaga),
    yield takeLatest(renameRecipe.started, renameRecipeSaga),
    yield takeLatest(getRecipeList.started, getRecipeListSaga),
    yield takeLatest(getFarmsListRequest.done, getRecipeListSaga),
    yield takeLatest([createRecipe.done, editRecipe.done, renameRecipe.done, deleteRecipe.done], refetchFeedingsSaga),
  ]);
}
